import { create } from 'zustand';
import subscriptionService from '../services/subscription.service';

/**
 * User Features Store
 * Quản lý features của user dựa trên subscription hiện tại
 * CHỈ gọi API một lần khi user đăng nhập hoặc khi app load
 */
const useUserFeaturesStore = create((set, get) => ({
  // State
  subscription: null,
  features: [],
  uiElements: [], // Tất cả UI elements user có quyền truy cập
  loading: false,
  error: null,
  initialized: false, // Đã fetch lần đầu chưa

  // Actions
  /**
   * Fetch features của user từ API
   * CHỈ gọi một lần duy nhất, trừ khi force = true
   * @param {boolean} force - Bỏ qua cache và fetch lại
   */
  fetchUserFeatures: async (force = false) => {
    const { initialized, loading } = get();

    // Nếu đã initialized và không force -> skip
    if (initialized && !force) {
      return { features: get().features, uiElements: get().uiElements };
    }

    // Nếu đang loading -> skip để tránh gọi đồng thời
    if (loading) {
      return { features: get().features, uiElements: get().uiElements };
    }

    // Reset state trước khi fetch (đặc biệt quan trọng khi force=true hoặc user thay đổi)
    set({
      subscription: null,
      features: [],
      uiElements: [],
      loading: true,
      initialized: false,
      error: null
    });

    try {
      const response = await subscriptionService.getMyFeatures();
      const payload = response.payload || {};

      set({
        subscription: payload.subscription,
        features: payload.features || [],
        uiElements: payload.ui_elements || [],
        loading: false,
        initialized: true,
        error: null,
      });

      return { features: payload.features || [], uiElements: payload.ui_elements || [] };
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch user features',
        loading: false,
        initialized: true, // Đánh dấu đã thử fetch để không gọi lại
      });
      return { features: [], uiElements: [] };
    }
  },

  /**
   * Kiểm tra user có quyền xem element không
   * @param {string} elementName - Tên component/element
   * @returns {boolean}
   */
  canAccessElement: (elementName) => {
    const { uiElements } = get();
    // Nếu chưa load features, cho phép tạm thời (sẽ check lại sau)
    if (uiElements.length === 0) {
      return true;
    }
    const normalized = elementName?.toLowerCase();
    return uiElements.some(el => el?.toLowerCase() === normalized);
  },

  /**
   * Kiểm tra user có feature code không
   * @param {string} featureCode - Code của feature (VD: FEATURE_PORTFOLIO)
   * @returns {boolean}
   */
  hasFeature: (featureCode) => {
    const { features } = get();
    if (!featureCode) return false;
    const normalized = featureCode.toUpperCase();
    return features.some(f => f.code?.toUpperCase() === normalized);
  },

  /**
   * Lấy thông tin feature theo code
   * @param {string} featureCode - Code của feature
   * @returns {Object|null}
   */
  getFeature: (featureCode) => {
    if (!featureCode) return null;
    const { features } = get();
    const normalized = featureCode.toUpperCase();
    return features.find(f => f.code?.toUpperCase() === normalized) || null;
  },

  /**
   * Kiểm tra feature đã hết usage chưa
   * @param {string} featureCode - Code của feature
   * @returns {boolean} - true nếu đã hết usage
   */
  isUsageExhausted: (featureCode) => {
    const feature = get().getFeature(featureCode);
    if (!feature) return false;

    // Nếu không có limit, không bao giờ hết
    if (feature.limit_usage === null || feature.limit_usage === undefined || feature.limit_usage < 0) {
      return false;
    }

    // Hết usage khi used >= limit
    return (feature.usage_count || 0) >= feature.limit_usage;
  },

  /**
   * Kiểm tra có thể sử dụng feature không (có quyền + chưa hết usage)
   * @param {string} featureCode - Code của feature
   * @returns {boolean}
   */
  canUseFeature: (featureCode) => {
    const { hasFeature, isUsageExhausted } = get();
    
    // Check permission
    if (!hasFeature(featureCode)) return false;
    
    // Check usage
    return !isUsageExhausted(featureCode);
  },

  /**
   * Clear cache khi logout
   */
  clearCache: () => {
    set({
      subscription: null,
      features: [],
      uiElements: [],
      loading: false,
      initialized: false,
      error: null,
    });
  },
}));

export default useUserFeaturesStore;

